import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { attachProfilesByUserId } from "@/lib/admin-profiles";
import { REGISTRATION_FIELDS } from "@/lib/event-registration.server";

const RegisterForEventInput = z.object({
  eventId: z.string().uuid(),
  registrantName: z.string().min(1).max(200),
  registrantAge: z.number().int().min(1).max(120).nullable().optional(),
  phone: z.string().min(1).max(50),
  notes: z.string().max(2000).nullable().optional(),
});

export const registerForEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d) => RegisterForEventInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { registerForEventCore } = await import("@/lib/event-registration.server");
    return registerForEventCore(supabaseAdmin, context.userId, data);
  });

export const cancelEventRegistration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { cancelEventRegistrationCore } = await import("@/lib/event-registration.server");
    return cancelEventRegistrationCore(context.supabase, {
      id: data.id,
      ownerUserId: context.userId,
    });
  });

export const getMyEventRegistrations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("event_registrations")
      .select(
        `${REGISTRATION_FIELDS}, site_events(title, date_label, start_at, end_at, audience, details)`,
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getEventRegistrations = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ eventId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: registrations, error } = await supabase
      .from("event_registrations")
      .select(REGISTRATION_FIELDS)
      .eq("event_id", data.eventId)
      .eq("status", "confirmed")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    return attachProfilesByUserId(supabase, registrations ?? [], "user_id");
  });

export const adminCancelEventRegistration = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { cancelEventRegistrationCore } = await import("@/lib/event-registration.server");
    return cancelEventRegistrationCore(context.supabase, { id: data.id });
  });

export const getEventRegistrationCounts = createServerFn({ method: "GET" })
  .validator((d) => z.object({ eventIds: z.array(z.string().uuid()) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { loadEventRegistrationCapacity } = await import("@/lib/event-registration.server");
    return loadEventRegistrationCapacity(supabaseAdmin, data.eventIds);
  });

export const getMyEventRegistrationIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("event_registrations")
      .select("event_id")
      .eq("user_id", userId)
      .eq("status", "confirmed");
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => r.event_id);
  });
